import { DistributorID } from 'fym-shared/src/smartlinks/catalog';
import { StreamingSeriesPoint, StreamingSourceSeries } from 'fym-shared/src/music24/streaming';
import { ArtistPortalDimensionSeries } from 'fym-shared/src/smartlinks/artistPortals';

export type StreamingChartMetric = 'streams' | 'listeners' | 'saves' | 'playlistAdds' | 'downloads';
export type StreamingChartSplit = 'total' | 'source' | 'store' | 'country';

/** The lines a split by store or country draws: the top values of the period. */
export const DIMENSION_LINES = 5;

export interface StreamingChartRow {
  date: string;
  [lineKey: string]: string | number | undefined;
}

export interface StreamingChartLine {
  key: string;
  /** A source id for a source line, a store name or a country code for a dimension line. */
  label: string;
  kind: 'total' | 'source' | 'dimension';
}

export interface StreamingChartData {
  rows: StreamingChartRow[];
  lines: StreamingChartLine[];
}

function addPoint(rows: Map<string, StreamingChartRow>, date: string, key: string, value: number): void {
  const row = rows.get(date) ?? { date };
  const before = row[key];
  row[key] = (typeof before === 'number' ? before : 0) + value;
  rows.set(date, row);
}

function sorted(rows: Map<string, StreamingChartRow>): StreamingChartRow[] {
  return [...rows.values()].sort((left, right) => left.date.localeCompare(right.date));
}

/**
 * The rows and lines of the Streaming chart (design b1).
 *
 * - `source`: one line per source, never a stack (design §4.2).
 * - `total`: one line. With a distributor, the streams and the downloads come from the
 *   distributor alone: the portal streams are never added to them.
 * - `store` / `country`: one line for each of the top values of the dimension series.
 *
 * A day with no number has no key, so the line breaks and does not fall to zero.
 */
export function buildStreamingChartData(input: {
  series: StreamingSourceSeries[];
  metric: StreamingChartMetric;
  split: StreamingChartSplit;
  hasDistributor: boolean;
  /** The sources the reader turned off. */
  off: readonly string[];
  dimensionSeries?: ArtistPortalDimensionSeries[];
}): StreamingChartData {
  const { metric, split, hasDistributor, off } = input;
  const rows = new Map<string, StreamingChartRow>();

  if (split === 'store' || split === 'country') {
    const dimensionMetric = metric === 'downloads' ? 'downloads' : 'streams';
    const top = (input.dimensionSeries ?? []).slice(0, DIMENSION_LINES);
    top.forEach((one, index) =>
      one.points.forEach((point) => {
        const value = point[dimensionMetric];
        if (typeof value === 'number') {
          addPoint(rows, point.date, `d${index}`, value);
        }
      })
    );
    return {
      rows: sorted(rows),
      lines: top.map((one, index) => ({ key: `d${index}`, label: one.value, kind: 'dimension' })),
    };
  }

  const shown = input.series.filter((one) => !off.includes(one.integrationType));
  const fromDistributorOnly = hasDistributor && (metric === 'streams' || metric === 'downloads');
  const feeding =
    split === 'total' && fromDistributorOnly
      ? shown.filter((one) => one.integrationType === DistributorID.Ditto)
      : shown;
  const withNumber = feeding.filter((one) =>
    one.points.some((point: StreamingSeriesPoint) => typeof point[metric] === 'number')
  );

  withNumber.forEach((one) =>
    one.points.forEach((point) => {
      const value = point[metric];
      if (typeof value === 'number') {
        addPoint(rows, point.date, split === 'total' ? 'total' : one.integrationType, value);
      }
    })
  );
  if (split === 'total') {
    const total: StreamingChartLine[] =
      withNumber.length === 0 ? [] : [{ key: 'total', label: 'total', kind: 'total' }];
    return { rows: sorted(rows), lines: total };
  }
  return {
    rows: sorted(rows),
    lines: withNumber.map((one) => ({ key: one.integrationType, label: one.integrationType, kind: 'source' })),
  };
}
